import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, useMediaQuery, useTheme, Tabs, Tab, CircularProgress } from '@mui/material';
import { signOut, onAuthStateChanged, User } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebaseConfig';
import { CustomerDispatchPage } from './components/CustomerPage';
import { CUSTOMER_INFO_MAP, resolveCustomerIdFromUserDoc, isProcurementOnlyCustomer, type CustomerId } from './config/customerConfig';
import { useLocation } from 'react-router-dom';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PaymentIcon from '@mui/icons-material/Payment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import HomeIcon from '@mui/icons-material/Home';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CustomerProcurementPage from './components/CustomerProcurementPage';

type CustomerUserConfigState = {
  showStatsTab: boolean;
  showStatsInReport: boolean;
  itemNameFilterType: 'all' | 'allow' | 'block';
  itemNameList: string[];
  routeFilterType: 'all' | 'allow';
  routeList: string[];
};

function buildCustomerUserConfig(userData: Record<string, unknown> | undefined | null): CustomerUserConfigState {
  const itemNameFilterType: 'all' | 'allow' | 'block' =
    userData?.itemNameFilterType === 'allow' || userData?.itemNameFilterType === 'block'
      ? (userData.itemNameFilterType as 'allow' | 'block')
      : 'all';
  return {
    showStatsTab: !!userData?.showStatsTab,
    showStatsInReport: !!userData?.showStatsInReport,
    itemNameFilterType,
    itemNameList: Array.isArray(userData?.itemNameList)
      ? (userData!.itemNameList as unknown[]).map(x => String(x).trim()).filter(Boolean)
      : [],
    routeFilterType: userData?.routeFilterType === 'allow' ? 'allow' : 'all',
    routeList: Array.isArray(userData?.routeList)
      ? (userData!.routeList as unknown[]).map(x => String(x).trim()).filter(Boolean)
      : [],
  };
}

// 화주 정보 매핑을 위한 타입 (기존 타입과 호환성 유지)
interface LegacyCustomerInfo {
  name: string; 
  logoPath: string; 
  color: string;
  backgroundColor: string;
  gradientBackground: string;
  lightColor: string;
}

// config의 CustomerInfo를 기존 타입으로 변환하는 함수
const convertToLegacyFormat = (customerId: CustomerId): LegacyCustomerInfo => {
  const info = CUSTOMER_INFO_MAP[customerId];
  return {
    name: info.name,
    logoPath: info.logoPath,
    color: info.color,
    backgroundColor: info.backgroundColor,
    gradientBackground: info.backgroundColor,
    lightColor: info.lightColor
  };
};

// 카드형 메뉴 컴포넌트
const CustomerMenuCard = ({ 
  title, 
  icon, 
  onClick, 
  customerColor, 
  isComingSoon = false 
}: { 
  title: string; 
  icon: React.ReactNode; 
  onClick: () => void; 
  customerColor: string;
  isComingSoon?: boolean;
}) => (
  <Box 
    onClick={isComingSoon ? undefined : onClick}
    sx={{
      height: 120,
      cursor: isComingSoon ? 'default' : 'pointer',
      background: isComingSoon 
        ? 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
        : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      border: `2px solid ${isComingSoon ? '#dee2e6' : customerColor}20`,
      borderRadius: 3,
      boxShadow: isComingSoon 
        ? '0 2px 8px rgba(0,0,0,0.06)'
        : '0 4px 20px rgba(0,0,0,0.08)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      overflow: 'hidden',
      opacity: isComingSoon ? 0.6 : 1,
      '&:hover': isComingSoon ? {} : {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 32px ${customerColor}30`,
        borderColor: `${customerColor}40`,
        '& .card-icon': {
          transform: 'scale(1.1)',
          color: customerColor
        },
        '& .card-title': {
          color: customerColor
        }
      },
      '&:before': isComingSoon ? {} : {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: `linear-gradient(90deg, ${customerColor} 0%, ${customerColor}80 100%)`,
        opacity: 0,
        transition: 'opacity 0.3s ease'
      },
      '&:hover:before': isComingSoon ? {} : {
        opacity: 1
      }
    }}
  >
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      justifyContent: 'center', 
      alignItems: 'center',
      p: 2,
      textAlign: 'center'
    }}>
      <Box className="card-icon" sx={{ 
        color: isComingSoon ? '#adb5bd' : customerColor,
        mb: 1,
        transition: 'all 0.3s ease'
      }}>
        {icon}
      </Box>
      <Typography 
        className="card-title"
        variant="h6" 
        fontWeight={700}
        sx={{ 
          color: isComingSoon ? '#6c757d' : '#2c3e50',
          fontSize: { xs: 16, sm: 18 },
          transition: 'all 0.3s ease'
        }}
      >
        {title}
      </Typography>
    </Box>
  </Box>
);

function AppCustomer() {
  const [user, setUser] = useState<User | null>(null);
  const [customerInfo, setCustomerInfo] = useState<LegacyCustomerInfo | null>(null);
  const [customerId, setCustomerId] = useState<CustomerId | null>(null);
  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // 로그인 후 전달받은 userConfig (탭 노출, 품명·운송구간 필터)
  const [userConfig, setUserConfig] = useState<CustomerUserConfigState>(() => buildCustomerUserConfig(undefined));
  
  // 사용자 인증 상태 및 화주 정보 로드
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      
      if (user) {
        try {
          // 사용자 정보 로드
          const userDoc = await getDoc(doc(db, 'users', user.email!));
          const userData = userDoc.data();
          
          // 로컬 스토리지에서 userConfig 로드 (로그인 시 저장된 값)
          const savedConfig = localStorage.getItem('userConfig');
          if (savedConfig) {
            try {
              const parsed = JSON.parse(savedConfig) as Record<string, unknown>;
              setUserConfig(buildCustomerUserConfig(parsed));
            } catch {
              if (userData) {
                const config = buildCustomerUserConfig(userData as Record<string, unknown>);
                setUserConfig(config);
                localStorage.setItem('userConfig', JSON.stringify(config));
              }
            }
          } else if (userData) {
            const config = buildCustomerUserConfig(userData as Record<string, unknown>);
            setUserConfig(config);
            localStorage.setItem('userConfig', JSON.stringify(config));
          }
          
          // 화주 정보 설정 (users.customerId 우선 — 부서 서브계정 등)
          if (userData?.role === 'customer') {
            const resolvedId = resolveCustomerIdFromUserDoc(user.email!, userData.customerId);
            setCustomerId(resolvedId);
            const info = convertToLegacyFormat(resolvedId);
            setCustomerInfo(info);
          }
        } catch (error) {
          console.error('사용자 정보 로드 오류:', error);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  // Firestore에서 사용자 설정 실시간 조회 (관리자 페이지 변경사항 즉시 반영)
  useEffect(() => {
    if (user?.email) {
      const userDocRef = doc(db, 'users', user.email);
      
      // 실시간 리스너 등록
      const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          const newUserConfig = buildCustomerUserConfig(userData as Record<string, unknown>);
          setUserConfig(newUserConfig);
          localStorage.setItem('userConfig', JSON.stringify(newUserConfig));
        }
      }, (error) => {
        console.error('사용자 설정 실시간 조회 실패:', error);
      });

      return () => unsubscribe();
    }
  }, [user?.email]);

  // location.state에서 userConfig 로드 (로그인 시 전달받은 값)
  useEffect(() => {
    if (location.state && (location.state as any).userConfig) {
      const config = (location.state as any).userConfig;
      setUserConfig(config);
      localStorage.setItem('userConfig', JSON.stringify(config));
    }
  }, [location.state]);

  // 모바일에서 상태바 색상 전역 관리
  useEffect(() => {
    if (isMobile) {
      // 모바일에서만 상태바 색상을 흰색으로 변경
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', '#ffffff');
      }
      
      // iOS Safari 상태바 스타일 변경
      const metaAppleStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
      if (metaAppleStatusBar) {
        metaAppleStatusBar.setAttribute('content', 'default');
      }
    }
    
    // 컴포넌트 언마운트 시 원래 색상으로 복원
    return () => {
      if (isMobile) {
        const metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (metaThemeColor) {
          metaThemeColor.setAttribute('content', '#1976d2');
        }
        
        const metaAppleStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
        if (metaAppleStatusBar) {
          metaAppleStatusBar.setAttribute('content', 'default');
        }
      }
    };
  }, [isMobile]);

  // 동적 화면 구성 함수
  const getScreenConfig = () => {
    const screens = [
      { id: 'home', label: '홈' },
      { id: 'dispatch', label: '배차' },
      { id: 'settlement', label: '정산' },
    ];
    
    // 운송료 탭이 활성화된 경우에만 추가
    if (userConfig.showStatsTab) {
      screens.push({ id: 'stats', label: '운송료' });
    }
    
    // 보고서 탭은 항상 마지막에 추가
    screens.push({ id: 'report', label: '보고서' });
    
    return screens;
  };

  const screenConfig = getScreenConfig();

  // 홈 화면 컴포넌트를 먼저 선언
  const CustomerHomeScreen = () => (
    <Box sx={{ 
      minHeight: '100vh', 
      background: isMobile 
        ? `linear-gradient(135deg, ${customerInfo?.backgroundColor || '#f8f9fa'} 0%, ${customerInfo?.lightColor || '#e9ecef'} 100%)`
        : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      py: 4,
      px: 2
    }}>
      {/* 화주 로고 및 인사말 */}
      {customerInfo && (
        <Box sx={{ 
          mb: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          bgcolor: isMobile ? 'transparent' : customerInfo.backgroundColor,
          borderRadius: isMobile ? 0 : 4,
          p: isMobile ? 2 : 4,
          mx: 'auto',
          maxWidth: isMobile ? '100%' : 530,
          boxShadow: isMobile ? 'none' : '0 8px 32px rgba(0,0,0,0.1)',
          border: isMobile ? 'none' : `1px solid ${customerInfo.color}20`
        }}>
          <img 
            src={customerInfo.logoPath} 
            alt={customerInfo.name} 
            style={{ 
              width: '100%',
              maxWidth: isMobile ? 120 : 140,
              height: isMobile ? 60 : 70, 
              objectFit: 'contain',
              objectPosition: 'center',
              marginBottom: isMobile ? 12 : 8,
              borderRadius: isMobile ? 0 : 8
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <Typography 
            variant="h4" 
            fontWeight={700} 
            sx={{ 
              color: customerInfo.color,
              mb: 0.5,
              textAlign: 'center',
              textShadow: isMobile ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
              fontSize: isMobile ? 28 : { xs: 24, sm: 32 }
            }}
          >
            {customerInfo.name}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: isMobile ? customerInfo.color : '#666',
              fontSize: isMobile ? 16 : { xs: 14, sm: 16 },
              fontWeight: isMobile ? 600 : 500,
              textAlign: 'center'
            }}
          >
            ( 성우로지스 )
          </Typography>
        </Box>
      )}

      {/* PC용 메뉴 카드 그리드 (모바일에서는 숨김) */}
      {!isMobile && (
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: userConfig.showStatsTab ? '1fr 1fr' : '1fr',
            gap: 3
          }}>
            <CustomerMenuCard
              title="배차"
              icon={<AssignmentIcon sx={{ fontSize: 36 }} />}
              onClick={() => handleMenuSelect('dispatch')}
              customerColor={customerInfo?.color || '#1976d2'}
            />
            <CustomerMenuCard
              title="정산"
              icon={<PaymentIcon sx={{ fontSize: 36 }} />}
              onClick={() => handleMenuSelect('settlement')}
              customerColor={customerInfo?.color || '#1976d2'}
            />
            {/* 운송료탭 노출 여부 분기 */}
            {userConfig.showStatsTab && (
              <CustomerMenuCard
                title="운송료"
                icon={<AttachMoneyIcon sx={{ fontSize: 36 }} />}
                onClick={() => handleMenuSelect('stats')}
                customerColor={customerInfo?.color || '#1976d2'}
              />
            )}
            <CustomerMenuCard
              title="보고서"
              icon={<DescriptionIcon sx={{ fontSize: 36 }} />}
              onClick={() => handleMenuSelect('report')}
              customerColor={customerInfo?.color || '#1976d2'}
            />
          </Box>
        </Box>
      )}

      {/* 하단 로그아웃 버튼 */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          variant="outlined"
          onClick={async () => {
            try {
              // 로컬 스토리지 정리
              localStorage.removeItem('userConfig');
              await signOut(auth);
              window.location.href = '/';
            } catch (error) {
              console.error('로그아웃 오류:', error);
            }
          }}
          sx={{
            borderColor: '#dee2e6',
            color: '#6c757d',
            fontWeight: 600,
            px: 3,
            py: 1,
            borderRadius: 2,
            '&:hover': {
              borderColor: '#adb5bd',
              bgcolor: '#f8f9fa'
            }
          }}
        >
           로그아웃
        </Button>
      </Box>
    </Box>
  );

  // 동적 화면 배열 구성
  const getScreenComponents = () => {
    const screens = [
      // 0: 홈 화면
      <Box key="home" sx={{ height: '100%', overflow: 'auto' }}>
        <CustomerHomeScreen />
      </Box>,
      
      // 1: 배차 탭
              <Box key="dispatch" sx={{ 
          height: '100%', 
          overflow: 'hidden',
          ...(isMobile && {
            height: 'calc(100vh - 70px)', // 하단 네비게이션 바 높이 제외
            minHeight: 'calc(100vh - 70px)'
          })
        }}>
        <CustomerDispatchPage 
          onNavigateToMain={() => setCurrentScreenIndex(0)} 
          initialTabIndex={0}
          showStatsTab={userConfig.showStatsTab}
          showStatsInReport={userConfig.showStatsInReport}
          itemNameFilterType={userConfig.itemNameFilterType}
          itemNameList={userConfig.itemNameList}
          routeFilterType={userConfig.routeFilterType}
          routeList={userConfig.routeList}
          currentScreenIndex={currentScreenIndex}
          screenConfig={screenConfig}
          hideTopTabs={isMobile}
        />
      </Box>,
      
      // 2: 정산 탭
      <Box key="settlement" sx={{ height: '100%', overflow: 'hidden' }}>
        <CustomerDispatchPage 
          onNavigateToMain={() => setCurrentScreenIndex(0)} 
          initialTabIndex={1}
          showStatsTab={userConfig.showStatsTab}
          showStatsInReport={userConfig.showStatsInReport}
          itemNameFilterType={userConfig.itemNameFilterType}
          itemNameList={userConfig.itemNameList}
          routeFilterType={userConfig.routeFilterType}
          routeList={userConfig.routeList}
          currentScreenIndex={currentScreenIndex}
          screenConfig={screenConfig}
          hideTopTabs={isMobile}
        />
      </Box>,
    ];
    
    // 운송료 탭이 활성화된 경우에만 추가
    if (userConfig.showStatsTab) {
      screens.push(
        <Box key="stats" sx={{ height: '100%', overflow: 'hidden' }}>
          <CustomerDispatchPage 
            onNavigateToMain={() => setCurrentScreenIndex(0)} 
            initialTabIndex={2}
            showStatsTab={userConfig.showStatsTab}
            showStatsInReport={userConfig.showStatsInReport}
            itemNameFilterType={userConfig.itemNameFilterType}
            itemNameList={userConfig.itemNameList}
            routeFilterType={userConfig.routeFilterType}
            routeList={userConfig.routeList}
            currentScreenIndex={currentScreenIndex}
            screenConfig={screenConfig}
            hideTopTabs={isMobile}
          />
        </Box>
      );
    }

    // 보고서 탭은 항상 마지막에 추가
    screens.push(
      <Box key="report" sx={{ height: '100%', overflow: 'hidden' }}>
        <CustomerDispatchPage 
          onNavigateToMain={() => setCurrentScreenIndex(0)} 
          initialTabIndex={userConfig.showStatsTab ? 3 : 2}
          showStatsTab={userConfig.showStatsTab}
          showStatsInReport={userConfig.showStatsInReport}
          itemNameFilterType={userConfig.itemNameFilterType}
          itemNameList={userConfig.itemNameList}
          routeFilterType={userConfig.routeFilterType}
          routeList={userConfig.routeList}
          currentScreenIndex={currentScreenIndex}
          screenConfig={screenConfig}
          hideTopTabs={isMobile}
        />
      </Box>
    );
    
    return screens;
  };

  const screenComponents = getScreenComponents();

  // 메뉴 선택 핸들러 (홈에서 특정 탭으로 이동)
  const handleMenuSelect = (menu: 'dispatch' | 'settlement' | 'stats' | 'report') => {
    const targetIndex = screenConfig.findIndex(screen => screen.id === menu);
    if (targetIndex !== -1) {
      setCurrentScreenIndex(targetIndex);
    }
  };

  // 컴포넌트 간 네비게이션 이벤트 리스너
  useEffect(() => {
    const handleNavigateToScreen = (event: CustomEvent) => {
      const targetIndex = event.detail;
      if (targetIndex >= 0 && targetIndex < screenComponents.length) {
        setCurrentScreenIndex(targetIndex);
      }
    };

    window.addEventListener('navigateToScreen', handleNavigateToScreen as EventListener);
    return () => {
      window.removeEventListener('navigateToScreen', handleNavigateToScreen as EventListener);
    };
  }, [screenComponents.length]);

  // 로딩 중 텍스트/스피너 숨김: 상위에서 보여지는 로고만 유지되도록 null 반환
  if (!user || !customerInfo || !customerId) {
    return null;
  }

  // 오뚜기조달부: 출고조회 + 송장 뷰어
  if (isProcurementOnlyCustomer(customerId)) {
    return <CustomerProcurementPage customerInfo={customerInfo} />;
  }

  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      bgcolor: '#ffffff'
    }}>
      {/* PC용 상단 탭 네비게이션 제거 (중복 해결) */}
      {/* {!isMobile && (
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          bgcolor: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          zIndex: 1000
        }}>
          <Tabs 
            value={currentScreenIndex} 
            onChange={(e, newValue) => setCurrentScreenIndex(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minHeight: 64,
                fontSize: '1rem',
                fontWeight: 600,
                color: '#666',
                '&.Mui-selected': {
                  color: customerInfo?.color || '#1976d2',
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: customerInfo?.color || '#1976d2',
                height: 3
              }
            }}
          >
            {screenConfig.map((screen, index) => (
              <Tab 
                key={screen.id} 
                label={screen.label}
                icon={index === 0 ? <HomeIcon /> : undefined}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>
      )} */}

      {/* 메인 콘텐츠 영역 */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'hidden',
        position: 'relative',
        bgcolor: '#ffffff',
        // 터치 스크롤 안정성 개선
        touchAction: 'pan-y',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain'
      }}>
        {screenComponents[currentScreenIndex]}
      </Box>

      {/* 모바일용 하단 네비게이션 바 */}
      {isMobile && (
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'white',
          borderTop: '1px solid #e0e0e0',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          height: 70,
          px: 1
        }}>
          {screenConfig.map((screen, index) => (
            <Box
              key={screen.id}
              onClick={() => setCurrentScreenIndex(index)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                py: 1,
                cursor: 'pointer',
                color: currentScreenIndex === index ? customerInfo?.color || '#1976d2' : '#999',
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: customerInfo?.color || '#1976d2',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <Box sx={{ 
                fontSize: 24, 
                mb: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& svg': {
                  color: 'inherit !important'
                }
              }}>
                {index === 0 && <HomeIcon />}
                {screen.id === 'dispatch' && <AssignmentIcon />}
                {screen.id === 'settlement' && <PaymentIcon />}
                {screen.id === 'stats' && <AttachMoneyIcon />}
                {screen.id === 'procurement' && <LocalShippingIcon />}
                {screen.id === 'report' && <DescriptionIcon />}
              </Box>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '0.7rem',
                  fontWeight: currentScreenIndex === index ? 600 : 400,
                  textAlign: 'center',
                  color: 'inherit !important'
                }}
              >
                {screen.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* 모바일에서 하단 네비게이션 바 공간 확보 */}
      {isMobile && <Box sx={{ height: 70 }} />}
    </Box>
  );
}

export default AppCustomer; 